import { useManagementGetUnitsCancelledNonProcessedReport } from '@apps-common/apis/management/reporting'
import {
  useManagementGetNonProcessedSimCount,
  useManagementUpdateAllCancelledNonProcessed
} from '@apps-common/apis/management/unit-cancelled'
import { ButtonRoundedWithIcon, INPUT_INFO_SM_NO_BORDER, InputInfoSM } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, KeyValueCheckboxModal, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { toastWarning } from '@apps-common/lib/toast'
import { ISelectOption } from '@apps-common/types/form'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { createAndDownloadExcel, getAvailablePropertiesForUnitsReport } from './generate-xlsx'

export const ReportingNonProcessedSims = () => {
  const [reportDownloaded, setReportDownloaded] = useState<boolean>(false)
  const [selections, setSelections] = useState<ISelectOption[]>([])

  const { data, isLoading, isFetching, isRefetching, isError, refetch } = useManagementGetNonProcessedSimCount({})
  const { mutate: getNonProcessed, isPending: isGettingNonProcessed } = useManagementGetUnitsCancelledNonProcessedReport(true, (data) => {
    createAndDownloadExcel([{ status: 'non-processed sims', data }], 'non_processed_cancelled_sims', moment().toISOString(), selections)
    setSelections([])
  })
  const { mutate: processNow, isPending: isProcessing } = useManagementUpdateAllCancelledNonProcessed(true, () => refetch())

  useEffect(() => {
    if (selections && selections.length > 0) {
      getNonProcessed()
    }
  }, [selections])

  const loading = useMemo(
    () => isLoading || isFetching || isRefetching || isError || isProcessing || isGettingNonProcessed || isProcessing,
    [isError, isFetching, isRefetching, isLoading, isProcessing, isGettingNonProcessed, isProcessing]
  )

  return (
    <div className='flex flex-col sm:flex-row sm:items-end justify-between gap-[20px] sm:gap-[10px]'>
      <InputInfoSM
        id='nonProcessedCount'
        label='Non-processed count'
        labelClassName='ml-0 mb-1 rounded-sm jost-medium'
        containerClassName='!mb-0 min-w-[240px] grow'
        className={INPUT_INFO_SM_NO_BORDER}
        loading={loading}
        hideNoEditIcon
        info='Report download required prior to processing sims.'
        value={<div className='flex items-center justify-start gap-[5px] !text-text underline'>{data?.resultCount} non-processed sims</div>}
      />
      <div className='sm:w-auto flex items-center justify-end gap-[10px]'>
        <ModalButton
          component={KeyValueCheckboxModal}
          disabled={loading}
          classes='w-full sm:w-auto sm:grow-0'
          componentProps={{
            keyValues: getAvailablePropertiesForUnitsReport()
          }}
          confirmCallback={(selectionData) => {
            if (Array.isArray(selectionData) && selectionData.length > 0) {
              setReportDownloaded(true)
              setSelections(selectionData)
            } else {
              toastWarning('No properties selected. Please select at least one property to include in the report.')
            }
          }}
        >
          <ButtonRoundedWithIcon
            type='div'
            text='Report'
            icon={<commonIcons.arrowAngled />}
            disabled={loading}
            className='action-success'
            iconContainerClassName='bg-success text-on-success'
          />
        </ModalButton>
        <ModalButton
          component={ConfirmModal}
          disabled={loading || !reportDownloaded}
          classes='w-full sm:w-auto sm:grow-0'
          componentProps={{
            text: 'Process',
            description: `Confirm you would like to process ${data?.resultCount} non-processed cancelled sims? This cannot be un-done.`,
            action: ConfirmModalAction.Warning
          }}
          confirmCallback={() => processNow()}
        >
          <ButtonRoundedWithIcon
            type='div'
            text='Process'
            icon={<commonIcons.arrowRight />}
            disabled={loading}
            className='action-warning'
            iconContainerClassName='bg-warning text-on-warning'
          />
        </ModalButton>
      </div>
    </div>
  )
}
